import type { PlaybackTransport } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeSummary } from "@/api";
import {
  createPlayerTimelineViewport,
  MultiChannelVideoPlayer,
  PLAYBACK_TIMELINE_ROW,
  type PlayerTimelineTrack,
  type PlayerViewportSource,
  type VideoChannelSource,
  type VideoGridFit,
} from "@/components/video";
import {
  episodePlaybackRateStore,
  useEpisodePlaybackRate,
  useEpisodeReviewMode,
} from "@/components/dataset/annotation";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { episodeFilesStepSeconds, episodeFilesToChannels } from "../../episodeChannels";
import type { FileBrowserSource } from "../../fileBrowserSource";
import { useEpisodeFiles } from "../../useEpisodeFiles";
import styles from "../FileBrowser.module.css";

// 채널 없음(파일 미적재)일 때 쓰는 고정 빈 배열. 매 렌더의 새 `[]` 가 재생 엔진을 다시 설정하지
// 않게 한다.
const EMPTY_CHANNELS: readonly VideoChannelSource[] = [];
// 카메라 격자의 크기 규칙. 두 탭이 같은 fit 을 쓰고 값만 다르다. 하한 아래에서는 격자가 자기
// 상자 안에서 스크롤한다. 그래서 리뷰 독이 펼쳐져도 영상이 하한 아래로 줄지 않는다.
// - 리뷰: 영상이 본문의 주 요소라서 상자가 허용하는 만큼 키운다(열은 c6 auto-fit 그대로).
// - 분석: 그래프가 폭을 가져야 하므로 10rem 을 넘기지 않는다. 카메라를 한 열로 쌓고, 높이가
//   모자라면 줄인다. 영상 열 폭이 이 높이를 따라가므로 폭을 보지 않는 `single` 을 쓴다.
//   그래서 폭과 높이가 서로를 바꾸는 순환이 없다.
const REVIEW_FIT: VideoGridFit = { columns: "auto", floorRem: 6, capRem: null };
const ANALYSIS_FIT: VideoGridFit = { columns: "single", floorRem: 6, capRem: 10 };
// 분석 배치의 영상 열 폭이 읽는 변수. `FileBrowser.module.css` 의 `.episodePane[data-layout="analysis"]`.
const ANALYSIS_FRAME_HEIGHT_VAR = "--analysis-video-frame-height";
// 행 타임라인의 기본 행. 재생 위치만 있는 한 줄이다. 구간 행이 붙기 전에도 타임라인 높이가 같다.
const PLAYBACK_ROW: readonly PlayerTimelineTrack[] = [PLAYBACK_TIMELINE_ROW];
// 리뷰 탭의 리뷰 모드는 기본 5x다. 뷰 모드와 분석 탭은 기본 1x다.
// 사용자가 직접 선택한 배속은 탭과 모드를 바꿔도 유지한다.
const REVIEW_PLAYBACK_RATE = 5;
const VIEW_PLAYBACK_RATE = 1;

/**
 * 영상 아래 슬롯(`renderExtra`)이 받는 재생 핸들. 영상은 이 컴포넌트가 소유한다. 슬롯은
 * 명령과 행만 보낸다.
 */
export type EpisodePlayback = {
  // 영상이 아직 없거나 언마운트됐으면 명령을 조용히 무시한다.
  seekToSeconds: (seconds: number) => void;
  // 재생 위치를 구독할 핸들. 영상이 준비되기 전에는 null 이다.
  transport: PlaybackTransport | null;
  // 행 타임라인이 그리는 시간 창을 구독할 핸들. 핸들은 항상 있다(스냅샷이 null 이면
  // "창 없음"). identity 가 고정이므로 창이 움직여도 이 슬롯을 다시 그리지 않는다. 창은
  // 타임라인을 끄는 동안 매 프레임 바뀐다. 그 변화가 영상 그리드를 다시 그리게 하면 안 된다.
  viewport: PlayerViewportSource;
  // 이 에피소드의 타임라인 행을 등록한다. 떠난 에피소드 uuid 로 등록한 행은 무시한다. null 이면
  // 기본 재생 행으로 돌아간다. 행 배열은 안정 참조여야 한다.
  setTimelineTracks: (episodeUuid: string, tracks: readonly PlayerTimelineTrack[] | null) => void;
  // 재생 바 바로 위 영역. 슬롯이 칩 줄을 portal 로 놓는다. 영역이 아직 마운트되지 않았으면 null.
  transportLead?: HTMLElement | null;
};

/**
 * 선택 에피소드의 카메라 그리드 + (주입된) 진단 패널.
 *
 * 리뷰·분석 두 탭이 **같은 플레이어 구성**을 쓴다.
 * - 격자만 자기 상자 안에서 스크롤한다(compact).
 * - 사용자가 영상 채널을 골라 숨길 수 있다.
 * - 재생 바는 행을 쌓는 타임라인(action_source 출처 행)이고, pane 하단 블록에 둔다.
 * 탭이 바꾸는 것은 배치(`analysisLayout`: 영상 열 | 진단 열)뿐이다. 그래서 탭을 바꿔도 격자와
 * 바를 다시 마운트하지 않는다.
 *
 * 진단 열은 리뷰에서 숨기되 마운트는 유지한다. 칩 줄과 출처 행의 소유자가 그 슬롯 안에 있다.
 *
 * 이 컴포넌트는 플레이어가 알려 준 transport 도 보관한다. 진단 그래프에서 구간을 눌러 그
 * 장면으로 영상을 옮기는 경로가 여기를 거친다.
 */
export function EpisodePreview({
  source,
  episode,
  analysisLayout = false,
  transportBar,
  transportLead,
  renderExtra,
}: {
  source: FileBrowserSource;
  episode: EpisodeSummary;
  analysisLayout?: boolean;
  // pane 하단 블록의 재생 바 영역과 그 바로 위(칩) 영역. `PreviewPane` 이 소유한다. 생략하면
  // 재생 바를 영상 바로 아래에 그린다.
  transportBar?: HTMLElement | null;
  transportLead?: HTMLElement | null;
  renderExtra?: (episode: EpisodeSummary, playback: EpisodePlayback) => ReactNode;
}): ReactNode {
  const { files, reportVideoError } = useEpisodeFiles(source, episode);
  // 영상이 아직 없거나(로딩·빈 채널) 언마운트됐으면 null 이다. 그러면 명령을 조용히 무시한다.
  // 그래프를 눌러도 아무 동작이 없는 편이 낫다. seek 는 ref 로 항상 최신 핸들을 호출한다. 구독할
  // 소비처를 위해 같은 핸들을 state 로도 보관한다. 핸들이 바뀌면 슬롯을 다시 그려야 하기 때문이다.
  const transportRef = useRef<PlaybackTransport | null>(null);
  const [transport, setTransport] = useState<PlaybackTransport | null>(null);
  const handleTransportReady = useCallback((next: PlaybackTransport | null) => {
    transportRef.current = next;
    setTransport(next);
  }, []);
  const seekToSeconds = useCallback((seconds: number) => {
    transportRef.current?.seekToSeconds(seconds);
  }, []);
  // 리뷰 모드는 5x, 뷰 모드와 분석 탭은 1x로 연다. transport 준비 후와 탭·모드 전환 시
  // 배속을 맞춘다. 초기값만 고정하면 뷰→리뷰 전환 때 1x 에 머문다. 단, 사용자가 배속을 직접 고른
  // 뒤부터는(manual) 그 값을 유지하고, 모드가 배속을 바꾸지 않는다.
  //
  // transport 의 배속 변경을 구독해 **사용자가 직접 골랐는지**를 구분한다.
  // - 이 코드가 `setPlaybackRate` 를 호출하는 동안(`applyingOwn`) 온 변화는 이 코드가 만든 것이다.
  // - 그 밖에 값이 실제로 달라졌다면 그 배속은 사용자의 manual set 이다.
  // transport 를 새로 받으면 현재 배속을 관측 기준으로 정한다. 초기 배속 설정이 알림을 생략해도
  // 첫 사용자 선택부터 감지해야 하기 때문이다.
  const mode = useEpisodeReviewMode();
  const playbackRatePref = useEpisodePlaybackRate();
  const playbackRef = useRef({ lastObserved: null as number | null, applyingOwn: false });
  const lastApply = useRef<{ transport: PlaybackTransport | null; rate: number } | null>(null);
  useEffect(() => {
    if (transport === null) return;
    const state = playbackRef.current;
    state.lastObserved = transport.getSnapshot().playbackRate;
    return transport.subscribe(() => {
      const rate = transport.getSnapshot().playbackRate;
      if (state.applyingOwn) {
        state.lastObserved = rate;
        return;
      }
      if (state.lastObserved !== null && rate !== state.lastObserved) {
        episodePlaybackRateStore.setManual(rate);
      }
      state.lastObserved = rate;
    });
  }, [transport]);
  useEffect(() => {
    if (transport === null) return;
    const target = playbackRatePref.manual
      ? playbackRatePref.rate
      : !analysisLayout && mode === "review"
        ? REVIEW_PLAYBACK_RATE
        : VIEW_PLAYBACK_RATE;
    const prev = lastApply.current;
    if (prev !== null && prev.transport === transport && prev.rate === target) return;
    lastApply.current = { transport, rate: target };
    const state = playbackRef.current;
    state.applyingOwn = true;
    try {
      transport.setPlaybackRate(target);
    } finally {
      state.applyingOwn = false;
    }
  }, [analysisLayout, mode, transport, playbackRatePref]);
  // 슬롯이 등록한 행. 어느 에피소드의 행인지 함께 보관한다. 에피소드를 넘기는 동안 떠난 슬롯의
  // 정리(null)가 새 에피소드의 행을 지우지 않게, uuid 가 같을 때만 지운다.
  const [pushedTracks, setPushedTracks] = useState<{
    episodeUuid: string;
    tracks: readonly PlayerTimelineTrack[];
  } | null>(null);
  const setTimelineTracks = useCallback(
    (episodeUuid: string, tracks: readonly PlayerTimelineTrack[] | null) => {
      setPushedTracks((current) => {
        if (tracks !== null) return { episodeUuid, tracks };
        return current?.episodeUuid === episodeUuid ? null : current;
      });
    },
    [],
  );
  // 창은 이 컴포넌트의 state 가 아니다. 창 store 하나를 보관해 재생 바와 슬롯에 같이 전달한다.
  // state 로 보관하면 타임라인을 끄는 매 프레임마다 아래 카메라 그리드까지 다시 조정된다. 플레이어는
  // 에피소드마다 다시 마운트된다. 새 바는 같은 store 에 붙어 기준 창으로 다시 연다.
  const [timelineViewport] = useState(createPlayerTimelineViewport);
  // 분석 배치의 영상 열 폭은 맞춘 프레임 높이를 따라간다. 플레이어는 에피소드마다 다시 마운트된다.
  // 그래서 값을 이 pane 에 두고 지우지 않는다. 지우면 에피소드를 넘길 때마다 열 폭이 튄다.
  const paneRef = useRef<HTMLDivElement>(null);
  const handleFrameFit = useCallback((px: number) => {
    paneRef.current?.style.setProperty(ANALYSIS_FRAME_HEIGHT_VAR, `${px}px`);
  }, []);
  const playback = useMemo<EpisodePlayback>(
    () => ({
      seekToSeconds,
      transport,
      setTimelineTracks,
      viewport: timelineViewport,
      transportLead,
    }),
    [seekToSeconds, transport, setTimelineTracks, timelineViewport, transportLead],
  );
  const timelineTracks =
    pushedTracks?.episodeUuid === episode.uuid ? pushedTracks.tracks : PLAYBACK_ROW;
  const channels = useMemo<readonly VideoChannelSource[]>(
    () => (files.status === "success" ? episodeFilesToChannels(files.data) : EMPTY_CHANNELS),
    [files],
  );
  // 한 칸을 한 프레임으로 맞춘다. 이 화면에서는 진단을 영상과 비교한다. 0.1초 고정 폭으로는
  // 프레임을 건너뛴다. fps 를 모르면 c6 기본값으로 둔다.
  const stepSeconds = files.status === "success" ? episodeFilesStepSeconds(files.data) : undefined;
  return (
    <div
      ref={paneRef}
      className={styles.episodePane}
      data-layout={analysisLayout ? "analysis" : "review"}
    >
      <div className={styles.episodeVideo}>
        {/* 영상이 올 자리를 같은 틀로 채우고 가운데에 안내를 둔다. 영상이 오면 칸이 움직이지 않는다. */}
        {files.status === "loading" || files.status === "idle" ? (
          <div className={`${stateFrameClassName} ${styles.videoFrame}`}>
            <LoadingState mode="plain">영상을 불러오고 있습니다.</LoadingState>
          </div>
        ) : files.status === "error" ? (
          <div className={`${stateFrameClassName} ${styles.videoFrame}`}>
            <EmptyState mode="plain" tone="error">
              {files.message}
            </EmptyState>
          </div>
        ) : (
          <MultiChannelVideoPlayer
            channels={channels}
            mode="compact"
            channelVisibility
            transportContainer={transportBar}
            onChannelError={reportVideoError}
            emptyMessage="재생할 카메라 영상이 없습니다."
            fit={analysisLayout ? ANALYSIS_FIT : REVIEW_FIT}
            onFrameFit={handleFrameFit}
            zoomable
            stepSeconds={stepSeconds}
            timelineTracks={timelineTracks}
            timelineViewport={timelineViewport}
            onTransportReady={handleTransportReady}
          />
        )}
      </div>
      <div
        className={styles.episodeExtra}
        aria-label={analysisLayout ? "에피소드 분석" : "그리퍼 보기"}
      >
        {renderExtra?.(episode, playback) ?? null}
      </div>
    </div>
  );
}

/**
 * 단일 카메라 영상. Files 모드의 video 채널 폴더에서 에피소드를 골랐을 때 쓴다.
 * 선택 에피소드의 파일 중 그 채널 영상만 골라 재생한다. 영상이 없으면 안내를 표시한다.
 */
export function ChannelVideoPreview({
  source,
  episode,
  channel,
}: {
  source: FileBrowserSource;
  episode: EpisodeSummary;
  channel: string;
}): ReactNode {
  const { files, reportVideoError } = useEpisodeFiles(source, episode);
  const channels = useMemo<readonly VideoChannelSource[]>(() => {
    if (files.status !== "success") return EMPTY_CHANNELS;
    return episodeFilesToChannels(files.data).filter(
      (channelSource) => channelSource.label === channel,
    );
  }, [files, channel]);

  if (files.status === "loading" || files.status === "idle") {
    return <LoadingState>영상을 불러오고 있습니다.</LoadingState>;
  }
  if (files.status === "error") {
    return <EmptyState tone="error">{files.message}</EmptyState>;
  }
  return (
    <div className={styles.episodePane}>
      <MultiChannelVideoPlayer
        channels={channels}
        onChannelError={reportVideoError}
        emptyMessage="재생할 카메라 영상이 없습니다."
      />
    </div>
  );
}
